import { AbstractModel } from "../abstract-model";
import { QuestionnaireResultSection } from "./questionnaire-result-section.model";
import { MongoHelper } from "../../helpers/mongo-helper";
import { LocalDateTime } from "../local-date-time/local-date-time";
import { AbstractObject } from "../abstract-object";

/**
 * A kérdőív-felfüggesztés adatstruktúrája.
 */
export class QuestionnaireResultSuspend extends AbstractObject {
    /** A felfüggesztés időpontja. */
    suspendedAt: LocalDateTime = null;
    /** A folytatás időpontja. */
    resumedAt: LocalDateTime = null;

    constructor(data?: any) {
        super();
        super.init(data, this);
        if (data) {
            if (data.suspendedAt) {
                this.suspendedAt = new LocalDateTime(data.suspendedAt);
            }
            if (data.resumedAt) {
                this.resumedAt = new LocalDateTime(data.resumedAt);
            }
        }
    }
}

/**
 * A kérdőív-kitöltés állapotainak definíciója.
 */
export enum QuestionnaireResultState {
    /** A kérdőív-kitöltéshez szükséges adastruktúra elkészült. */
    Created = 0,
    /** A kliens jelezte, hogy a kitöltés lezárult. */
    Finished = 1,
    /** A kérdőív kitöltése megszakadt (pl. a felhasználó nem akarta kitölteni). */
    Cancelled = 2,
    /** A kitöltés érvénytelenítve lett. */
    Revoked = 3
}

/**
 * A kérdőív-eredmény adatstruktúrása.
 */
export class QuestionnaireResult extends AbstractModel {

    public static get collection() {
        if (Meteor.isServer)
            return MongoHelper.mongoDb.collection<QuestionnaireResult>('questionnaire-result');
        else
            throw new Meteor.Error("Collection is not available from  client code. Use the appropriate service classs instead.")
    }

    /***************************** */
    /** QuestionnaireResult fields */
    /***************************** */

    /**
     * Annak az kérdőív-élesítésnek az ID-ja, ami alapján ez az objektum  készült.
     * - kötelező
     * - érvényes ObjectID
     * - létezik ilyen ID-val Questionnaire a DB-ben.
     */
    questionnaireId: string = null;

    /**
     * Annak a felhasználónak az id-ja, aki majd kitölti a kérdőívet. (Meteor.users -> _id)
     * - kötelező
     * - létező felhasználó ID-ja
     */
    userId: string = null;

    /**
     * Ez mutatja meg, hogy a kérdőív melyik időzítéséhez tartozik a kérdőív-eredmény.
     * - kötelező (lehet 0)
     * - nem lehet 0-nál kisebb
     * - 0, ha nincs a kérdőíven időzítés (kérdőíven a scheduled=false)
     * - ha nem 0, akkor léteznie kelle egy ilyen order értékű időzítésnek a kérdőíven
     */
    scheduleOrder: number = null;

    /**
     * Ettől az időponttól kezdve lehet csak kitölteni a kérdőívet.
     * - kötelező
     * - ha van időzítés, akkor az épp aktuális alapján kell ezt beállítani
     * - ha nincs időzítés, akkor a kérdőív activeFrom értéke kerül ide
     */
    scheduledAt: LocalDateTime = null;

    /**
     * A kérdőívet csak eddig az időpontig lehet kitölteni.
     * Ezen túl már nem érkezhet be kitöltés a erre a kérdőívre (kliens oldalon is le kell kezelni).
     * Számítható a kérdőív időzítési értékeiből.
     * - kötelező (lehet null)
     * - ha van időzítés, akkor az épp aktuális alapján kell ezt beállítani
     * - ha nincs időzítés, akkor a kérdőív activeTo értéke kerül ide
     */
    scheduledUntil: LocalDateTime = null;

    /**
     * A kérdőív-kitöltés állapota.
     * - kötelező (lehet 0)
     * - csak 0 (Created) lehet (átállítását a regisztrált függvények végzik)
     */
    state?: QuestionnaireResultState = QuestionnaireResultState.Created;

    /**
     * A kérdőív-kitöltés kezdetének időpontja.
     * - 'Finished' státusz esetén kötelező
     * - a scheduledAt és a scheduledUntil közé kell essen
     */
    startedAt?: LocalDateTime = null;
    /**
     * A kérdőív-kitöltés befejezésének időpontja.
     * - 'Finished' státusz esetén kötelező
     * - nem lehet kisebb a startedAt értékénél
     * - a scheduledAt és a scheduledUntil közé kell essen
     */
    finishedAt?: LocalDateTime = null;

    /**
     * A kérdőív-szekciókhoz kapcsolódó válasz-szekciók.
     * - kötelező
     * - 'Finished' státusz esetén nem lehet üres
     * - minden elemének objektumnak kell lennie
     */
    sections?: QuestionnaireResultSection[] = [];

    /**
     * A kérdőív kitöltésének megszakításának és folytatásának időpontjai.
     *
     * Alapeset: Egy felfüggesztés-, majd egy folytatás-jelzés érkezik.
     *           Ekkor a felfüggesztés jelre létrejön egy új elem a tömbben
     *           kitöltve a 'suspendedAt' értékét, majd a folytatás jelzésre
     *           az utolsó tömb-elem 'resumedAt' értéke kerül kitöltésre.
     * Két felfüggesztés egymás után:
     *           !!! Kivétel - látható legyen, hogy már fel van függesztve
     * Két folytatás esemény jön egymás után:
     *           !!! Kivétel - látható legyen, hogy már újra van indítva
     */
    suspends?: QuestionnaireResultSuspend[] = [];

    /**
     * Ez jelzi, hogy az adatokat feldolgozta-e a rendszer (azaz lett-e belőle adatsor), és mikor.
     * Kérdőívhez nincs rá szükség -- 2023.01.05. AÁ
     */
    processedAt?: LocalDateTime;

    /**
     * Az osztály konstruktora.
     * @param data 
     */
    constructor(data?: any) {
        super();
        super.init(data, this);
        if (this.startedAt) {
            this.startedAt = new LocalDateTime(this.startedAt);
        }
        if (this.finishedAt) {
            this.finishedAt = new LocalDateTime(this.finishedAt);
        }
        if (this.scheduledAt) {
            this.scheduledAt = new LocalDateTime(this.scheduledAt);
        }
        if (this.scheduledUntil) {
            this.scheduledUntil = new LocalDateTime(this.scheduledUntil);
        }
        if (Array.isArray(this.suspends)) {
            this.suspends = this.suspends.map(item => new QuestionnaireResultSuspend(item));
        }
        if (Array.isArray(this.sections)) {
            this.sections = this.sections.map(item => new QuestionnaireResultSection(item));
        }
        //console.log(this);
    }
}
